
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Users } from "@/components/users/Users";
import { Organizations } from "@/components/organizations/Organizations";
import { useAuth } from "@/context/AuthContext";
import { RefreshCw, Copy } from "lucide-react";
import { useState, useEffect } from "react";
import { toast } from "@/hooks/use-toast";
import { useSubscriptionData } from "@/hooks/useSubscriptionData";

export default function Configuracoes() {
  const { subscriptionData, refreshSubscription } = useAuth();
  const { updateWebhookSecret } = useSubscriptionData();
  const [webhookSecret, setWebhookSecret] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  // Atualizar o estado local quando subscriptionData mudar
  useEffect(() => {
    setWebhookSecret(subscriptionData.webhook_secret || '');
  }, [subscriptionData.webhook_secret]);
  
  // Verificar se o usuário pode acessar as abas restritas
  const canAccessRestrictedTabs = subscriptionData.subscription_tier && 
                        subscriptionData.subscription_tier !== 'FREE' && 
                        subscriptionData.is_owner;

  const generateNewSecret = () => {
    setIsGenerating(true);
    try {
      // Gera novo GUID
      let newSecret: string;
      if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        newSecret = crypto.randomUUID();
      } else {
        // Fallback para ambientes que não suportam crypto.randomUUID()
        newSecret = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
          const r = Math.random() * 16 | 0;
          const v = c === 'x' ? r : (r & 0x3 | 0x8);
          return v.toString(16);
        });
      }
      
      setWebhookSecret(newSecret);
      toast({
        title: "Secret gerada",
        description: "Nova webhook secret foi gerada. Clique em 'Salvar Alterações' para persistir.",
      });
    } catch (error) {
      toast({
        title: "Erro",
        description: "Erro ao gerar nova secret. Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const copyToClipboard = async () => {
    if (!webhookSecret) {
      toast({
        title: "Nada para copiar",
        description: "Nenhuma webhook secret disponível para copiar.",
        variant: "destructive",
      });
      return;
    }

    try {
      await navigator.clipboard.writeText(webhookSecret);
      toast({
        title: "Copiado!",
        description: "Webhook secret copiada para a área de transferência.",
      });
    } catch (error) {
      toast({
        title: "Erro ao copiar",
        description: "Não foi possível copiar para a área de transferência.",
        variant: "destructive",
      });
    }
  };

  const handleSaveChanges = async () => {
    if (!webhookSecret) {
      toast({
        title: "Erro",
        description: "Nenhuma webhook secret para salvar.",
        variant: "destructive",
      });
      return;
    }

    setIsSaving(true);
    try {
      // Passar o subscription_id do contexto AuthContext
      const success = await updateWebhookSecret(webhookSecret, subscriptionData.subscription_id);
      if (success) {
        // Atualizar os dados da subscription para refletir a mudança
        await refreshSubscription();
        toast({
          title: "Configurações salvas",
          description: "Webhook secret foi salva com sucesso.",
        });
      } else {
        toast({
          title: "Erro ao salvar",
          description: "Não foi possível salvar a webhook secret. Tente novamente.",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Erro",
        description: "Erro inesperado ao salvar as configurações.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Configurações</h1>
        <p className="text-muted-foreground">
          Gerencie as configurações do sistema.
        </p>
      </div>
      
      <Tabs defaultValue="geral" className="space-y-6">
        <TabsList>
          <TabsTrigger value="geral">Geral</TabsTrigger>
          {canAccessRestrictedTabs && (
            <>
              <TabsTrigger value="usuarios">Usuários</TabsTrigger>
              <TabsTrigger value="organizacoes">Organizações</TabsTrigger>
            </>
          )}
        </TabsList>
        
        <TabsContent value="geral" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Geral</CardTitle>
              <CardDescription>
                Configurações gerais do sistema
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-2 max-w-md">
                <Label htmlFor="nome-sistema">Nome do Sistema</Label>
                <Input id="nome-sistema" defaultValue="Mozarch" />
              </div>
              <div className="grid gap-2 max-w-md">
                <Label htmlFor="idioma">Idioma Padrão</Label>
                <Select defaultValue="pt-br">
                  <SelectTrigger id="idioma">
                    <SelectValue placeholder="Selecione um idioma" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pt-br">Português (Brasil)</SelectItem>
                    <SelectItem value="en">English</SelectItem>
                    <SelectItem value="es">Español</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2 max-w-lg">
                <Label htmlFor="webhook-secret">Webhook Secret</Label>
                <div className="flex gap-2">
                  <Input 
                    id="webhook-secret" 
                    value={webhookSecret}
                    disabled
                    placeholder="Nenhuma secret gerada"
                    className="flex-1"
                  />
                  <Button 
                    type="button"
                    variant="outline" 
                    size="icon"
                    onClick={copyToClipboard}
                    disabled={!webhookSecret}
                    title="Copiar para área de transferência"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                  <Button 
                    type="button"
                    variant="outline" 
                    size="icon"
                    onClick={generateNewSecret}
                    disabled={isGenerating}
                    title="Gerar nova secret"
                  >
                    <RefreshCw className={`h-4 w-4 ${isGenerating ? 'animate-spin' : ''}`} />
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Secret GUID usada para validação de webhooks. Clique no ícone para gerar uma nova.
                </p>
              </div>
            </CardContent>
            <CardFooter>
              <Button onClick={handleSaveChanges} disabled={isSaving || !webhookSecret}>
                {isSaving ? 'Salvando...' : 'Salvar Alterações'}
              </Button>
            </CardFooter>
          </Card>
        </TabsContent>
        
        {canAccessRestrictedTabs && (
          <>
            <TabsContent value="usuarios" className="space-y-6">
              <Users />
            </TabsContent>
            
            <TabsContent value="organizacoes" className="space-y-6">
              <Organizations />
            </TabsContent>
          </>
        )}
      </Tabs>
    </div>
  );
}
